/**
 * Service Worker Registration Utility
 * Handles service worker registration, updates, and lifecycle events
 */

let registration = null;
let updateAvailable = false;
let updateCallbacks = [];

/**
 * Register service worker and handle updates
 * @param {Function} onUpdateAvailable - Callback when update is available
 * @param {Function} onUpdateSuccess - Callback when update is successful
 */
export async function registerSW(onUpdateAvailable, onUpdateSuccess) {
  // Check if service workers are supported
  if (!('serviceWorker' in navigator)) {
    console.log('Service Workers are not supported in this browser.');
    return;
  }


  try {
    // If there was no controller initially, this is the first time a SW is taking control.
    // Forcing a reload in that case makes the site "load twice" on first visit.
    const hadControllerInitially = !!navigator.serviceWorker.controller;

    // Check if service worker is already registered
    if (navigator.serviceWorker.controller) {
      registration = await navigator.serviceWorker.getRegistration();
    }
    
    // Register service worker if not already registered
    if (!registration) {
      // Use the official Vite PWA registration method for dev/prod compatibility
      const { registerSW: register } = await import('virtual:pwa-register');
      
      const updateSW = register({
        onNeedRefresh() {
          console.log('New service worker available');
          updateAvailable = true;
          updateCallbacks.forEach(callback => callback(true));
          if (onUpdateAvailable) onUpdateAvailable();
        },
        onOfflineReady() {
          console.log('App ready for offline use');
          if (onUpdateSuccess) onUpdateSuccess();
        },
      });

      // Get the standard registration object from the browser
      registration = await navigator.serviceWorker.getRegistration();
      if (!registration) {
        // Fallback for immediate registration access
        registration = await navigator.serviceWorker.ready;
      }
    } else {
      // Update existing registration
      await registration.update();
    }

    console.log('Service Worker registered successfully:', registration.scope);

    // Handle service worker updates
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      
      if (!newWorker) return;

      newWorker.addEventListener('statechange', () => {
        if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
          // New service worker available
          updateAvailable = true;
          console.log('New service worker available');
          
          // Notify all callbacks
          updateCallbacks.forEach(callback => callback(true));
          if (onUpdateAvailable) {
            onUpdateAvailable();
          }
        } else if (newWorker.state === 'activated') {
          // Service worker activated
          console.log('Service Worker activated');
          if (onUpdateSuccess) {
            onUpdateSuccess();
          }
        }
      });
    });

    // Check for updates periodically
    setInterval(() => {
      if (registration) {
        registration.update();
      }
    }, 60 * 60 * 1000); // Check every hour

    // Listen for controller change (when new SW takes control)
    // Add a flag to prevent multiple reloads
    let isReloading = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      console.log('Service Worker controller changed');

      // Don't reload on first install / first-time control. It causes an unnecessary second load.
      if (!hadControllerInitially) {
        console.log('First service worker control detected (initial install) - skipping reload');
        return;
      }

      // Prevent multiple reloads
      if (!isReloading) {
        isReloading = true;
        // Mark that we're reloading due to SW update
        if (typeof window !== 'undefined' && window.sessionStorage) {
          sessionStorage.setItem('sw-update-reload', 'true');
        }
        // Small delay to ensure the new controller is ready
        setTimeout(() => {
      window.location.reload();
        }, 100);
      }
    });

    return registration;
  } catch (error) {
    console.error('Service Worker registration failed:', error);
    return null;
  }
}

/**
 * Check if update is available
 * @returns {boolean}
 */
export function isUpdateAvailable() {
  return updateAvailable;
}

/**
 * Skip waiting and activate new service worker
 */
export async function skipWaiting() {
  if (!registration || !registration.waiting) {
    console.log('No service worker waiting');
    return;
  }

  try {
    // Send message to waiting service worker to skip waiting
    registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    // Do not reload here.
    // Reload (when needed) is handled via the controllerchange event to avoid double reloads.
  } catch (error) {
    console.error('Error skipping waiting:', error);
  }
}

/**
 * Get service worker registration
 * @returns {ServiceWorkerRegistration|null}
 */
export function getRegistration() {
  return registration;
}

/**
 * Add callback for update availability
 * @param {Function} callback
 */
export function onUpdateAvailable(callback) {
  if (typeof callback === 'function') {
    updateCallbacks.push(callback);
  }
}

/**
 * Remove callback for update availability
 * @param {Function} callback
 */
export function offUpdateAvailable(callback) {
  updateCallbacks = updateCallbacks.filter(cb => cb !== callback);
}

/**
 * Unregister service worker (for development/testing)
 */
export async function unregister() {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.ready;
      const unregistered = await registration.unregister();
      if (unregistered) {
        console.log('Service Worker unregistered');
      }
    } catch (error) {
      console.error('Error unregistering service worker:', error);
    }
  }
}

